import {
  IFlight,
  IGroundEvent,
  IAircraftRowSetting,
  IHighlightFlightSettings,
  IFlightType,
} from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";

export const timeScales = {
  hour: 3600000,
  day: 86400000,
  week: 604800000,
  month: 2592000000,
  "3month": 7776000000,
  // year: 31536000000,
};

export const indexOfClosest = (nums: number[], target: number): number => {
  let closest = Number.MAX_SAFE_INTEGER;
  let index = 0;
  nums.forEach((num, i) => {
    const dist = Math.abs(target - num);
    if (dist < closest) {
      index = i;
      closest = dist;
    }
  });
  return index;
};

export const searchGroundEvents = (
  events: IGroundEvent[],
  searchTime: number,
  start: number,
  end: number
): IGroundEvent | null => {
  if (start > end) return null;

  const validEvents = [];
  for (let n = 0; n < events.length; n++) {
    const event = events[n];
    let left = new Date(event.actualStart).getTime();
    let right = new Date(event.actualEnd).getTime();
    if (left > right) {
      const temp = left;
      left = right;
      right = temp;
    }
    if (left <= searchTime && right > searchTime) validEvents.push(event);
  }

  if (validEvents.length == 0) return null;
  else if (validEvents.length == 1) return validEvents[0];
  else {
    let shortestTime;
    let shortestIndex;
    for (let n = 0; n < validEvents.length; n++) {
      const timeFrame = (validEvents[n].actualEnd as number) - (validEvents[n].actualStart as number);
      if (!shortestTime || timeFrame < shortestTime) {
        shortestTime = timeFrame;
        shortestIndex = n;
      }
    }
    return validEvents[shortestIndex];
  }
};

export const binarySearchAircraftEvents = (
  flights: IFlight[] | IGroundEvent[],
  searchTime: number,
  start: number,
  end: number,
  searchScheduled = false
): IFlight | IGroundEvent | null => {
  // Base Condtion
  if (start > end) return null;

  // Find the middle index
  const mid = Math.floor((start + end) / 2);
  const event = flights[mid];
  let left = isFlight(event)
    ? searchScheduled
      ? new Date(event.scheduledOut).getTime()
      : new Date(event.actualOut).getTime()
    : new Date(event.actualStart).getTime();
  let right = isFlight(event)
    ? searchScheduled
      ? new Date(event.scheduledIn).getTime()
      : new Date(event.actualIn).getTime()
    : new Date(event.actualEnd).getTime();

  // Compare mid with given key x
  if (left > right) {
    const temp = left;
    left = right;
    right = temp;
  }
  if (left <= searchTime && right > searchTime) return event;

  // If element at mid is greater than x,
  // search in the left half of mid
  if (right > searchTime) return binarySearchAircraftEvents(flights, searchTime, start, mid - 1, searchScheduled);
  // If element at mid is smaller than x,
  // search in the right half of mid
  else return binarySearchAircraftEvents(flights, searchTime, mid + 1, end, searchScheduled);
};

export interface IRGBA {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

export const hexToRgb = (hex: string): number[] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16) / 255.0, parseInt(result[2], 16) / 255.0, parseInt(result[3], 16) / 255.0]
    : [1.0, 1.0, 1.0];
};

/** Return true if string passed in is numereic */
const isNumeric = (str: string): boolean => {
  if (typeof str != "string") return false; // we only process strings!
  return (
    !isNaN(str as any) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    !isNaN(parseFloat(str))
  ); // ...and ensure strings of whitespace fail
};

/** Returns true if the flight number matches highlight pattern */
const flightNumberMatchesPattern = (flightNumber: number, patternRaw: string): boolean => {
  if (!patternRaw) {
    return true;
  }

  // trim any whitespace found in patterns
  const pattern = Array.isArray(patternRaw)
    ? patternRaw.map((s) => (typeof s === "string" ? s?.trim() : s))
    : patternRaw.trim();

  if (!pattern || pattern?.length === 0) {
    return true; // if no pattern is given show flight.
  }
  // If an older saved pattern is entered in as an array of numbers
  const testpatternArray: string[] = Array.isArray(pattern) ? pattern : pattern.split(","); // split all patterns into an array.
  for (let i = 0; i < testpatternArray.length; i++) {
    //@ts-ignore -- added this condition to support older saved conditions that might include an array of numbers.
    if (typeof testpatternArray[i] === "number" && flightNumber === (testpatternArray[i] as number)) {
      return true;
    } else if (
      typeof testpatternArray[i] === "string" &&
      isNumeric(testpatternArray[i]) &&
      flightNumber === parseInt(testpatternArray[i])
    ) {
      return true;
    } else if (typeof testpatternArray[i] === "string" && testpatternArray[i].includes("-")) {
      const [start, end] = testpatternArray[i].split("-");
      if (isNumeric(start) && isNumeric(end))
        if (flightNumber >= parseInt(start) && flightNumber <= parseInt(end)) {
          return true;
        }
    }
  }
  // If none of the patterns fail, return false.
  return false;
};

/**
 * Function which looks at a flight's data to see if it meets conditions defined in supplied array and returns an alpha value.
 * @param flight - IFlight data
 * @param highlightConditions - set of conditions that when met, will display with normal color, if not met will dim the flight or its label to assign opacity.
 * @param dimmedAlpha - option dimmed value, default is 0.2
 * @returns - decimal opacity value i.e 1.0
 */
export const getFlightAlphaValue = (flight: IFlight, highlightFlightSettings: IHighlightFlightSettings): number => {
  const { active, conditions, dimmSetting, _deskFlightIds } = highlightFlightSettings ?? {
    active: false,
    conditions: null,
    dimmSetting: 0.1,
  };
  const hasConditions =
    conditions?.flightNumber?.length > 0 ||
    conditions?.origin?.length > 0 ||
    conditions?.destination?.length > 0 ||
    conditions?.flightType?.length > 0 ||
    conditions?.dispatchDesk?.length > 0 ||
    conditions?.highlightUnassigned;
  let valid = true;
  if (active && hasConditions) {
    const { flightNumber, origin, destination, locationInclusive, flightType, dispatchDesk } = conditions;

    const showFlight = flightNumberMatchesPattern(flight?.flightNumber, flightNumber);
    if (locationInclusive) {
      if (
        showFlight &&
        (origin.includes(flight?.origin) || origin?.length === 0) && // AND used for locations
        (destination.includes(flight?.destination) || destination?.length === 0)
      ) {
        valid = true;
      } else {
        valid = false;
      }
    } else {
      // Ignore origin and destination filters if both are empty.
      if (showFlight && origin?.length === 0 && destination?.length === 0) {
        valid = true;
      } else if (
        showFlight &&
        (origin.includes(flight?.origin) || // OR used for locations
          destination.includes(flight?.destination))
      ) {
        valid = true;
      } else {
        valid = false;
      }
    }

    const currentFlightType = flight?.flightType as IFlightType;
    if (flightType?.length > 0) {
      if (valid && flightType.includes(currentFlightType?._id)) {
        valid = true;
      } else {
        valid = false;
      }
    }

    // Dispatch desk filter: if desk IDs are selected, only flights belonging to those desks remain highlighted
    if (dispatchDesk?.length > 0 && _deskFlightIds) {
      if (valid && _deskFlightIds.includes(flight?._id)) {
        valid = true;
      } else {
        valid = false;
      }
    }

    // Unassigned filter: highlight flights NOT on any dispatch desk
    const { _allDeskFlightIds } = highlightFlightSettings ?? {};
    if (conditions?.highlightUnassigned && _allDeskFlightIds) {
      if (valid && !_allDeskFlightIds.includes(flight?._id)) {
        valid = true;
      } else {
        valid = false;
      }
    }
  }
  return valid ? 1.0 : dimmSetting;
};

/**
 * Function that takes in a hex value and alpha and returns a css rbga string.
 * Used for assigning opacity values to labels used in the timeline in order to better highlighted certain flights.
 * @param hex -
 * @param alpha -
 * @returns css rgba string
 */
export const hexToCSSRGBA = (hex: string, alpha?: number): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `rgba(
        ${parseInt(result[1], 16)},
        ${parseInt(result[2], 16)},
        ${parseInt(result[3], 16)},
        ${alpha ?? 1.0}
      )`
    : "rgba(255,255,255,1)"; // default opaque white;
};

export const hexToIRGBA = (hex: string, alpha?: number): IRGBA => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        red: parseInt(result[1], 16) / 255.0,
        green: parseInt(result[2], 16) / 255.0,
        blue: parseInt(result[3], 16) / 255.0,
        alpha: alpha ?? 1.0,
      }
    : {
        red: 1.0,
        green: 1.0,
        blue: 1.0,
        alpha: 1.0,
      };
};

export const rgbToHex = (rgb: number) => {
  let hex = Number(rgb).toString(16);
  if (hex.length < 2) {
    hex = "0" + hex;
  }
  return hex;
};

export const fullColorHex = (r: number, g: number, b: number) => {
  const red = rgbToHex(r);
  const green = rgbToHex(g);
  const blue = rgbToHex(b);
  return red + green + blue;
};

/**
 * Return either black or white fex value based on based contrast to supplied hex color intput.
 */
export const invertHexColor = (hex: string): string => {
  if (!hex) {
    return "#000000";
  }
  if (hex.startsWith("#")) {
    hex = hex.slice(1);
  }
  // convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  if (hex.length !== 6) {
    throw new Error("Invalid HEX color.");
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const value = r * 0.299 + g * 0.587 + b * 0.114;
  return value > 165 ? "#000000" : "#FFFFFF";
};

export interface ITickInterval {
  timeMillis: number;
  label: string;
  isHighlighted: boolean;
}

const oneDayAsMillis = 8.64e7;
const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getMonthYearStr = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = months[date.getUTCMonth()];
  return `${month} ${year}`;
};

export const getDayTickIntervals = (
  dMinPMILLIS: number,
  dMaxPMILLIS: number,
  approxTickIntervalMILLIS: number
): [ITickInterval[], ITickInterval[]] => {
  const tickIntervalDAYS = calculateTickInterval_DAYS(approxTickIntervalMILLIS);
  const m = new Date(dMinPMILLIS);
  m.setUTCDate(1);
  m.setUTCHours(0);
  m.setUTCMinutes(0);
  m.setUTCSeconds(0);
  m.setUTCMilliseconds(0);
  const endTimePMILLIS = dMaxPMILLIS + oneDayAsMillis * tickIntervalDAYS;
  let currentMonth = m.getUTCMonth();

  const tickTimesPMILLIS: ITickInterval[] = [];
  const monthTicksMillis: ITickInterval[] = [
    {
      timeMillis: dMinPMILLIS,
      label: getMonthYearStr(m),
      isHighlighted: false,
    },
  ];

  while (m?.valueOf() <= endTimePMILLIS) {
    // ensure ticks always fall on the first day of the month
    const newMonth = m.getUTCMonth();
    if (newMonth !== currentMonth) {
      m.setUTCDate(1);
      const timeMillis = m.getTime();
      monthTicksMillis.push({
        timeMillis,
        label: getMonthYearStr(m),
        isHighlighted: true,
      });
      currentMonth = newMonth;
    }

    // prevent display of ticks too close to the end of the month
    const maxDom = new Date(m.getUTCFullYear(), m.getUTCMonth() + 1, 0).getDate(); // m.clone().endOf('month').date();
    const dom = m.getUTCDate();
    if (maxDom - dom + 1 >= tickIntervalDAYS / 2) {
      const daysStr = m.getUTCDate() < 10 ? `0${m.getUTCDate()}` : `${m.getUTCDate()}`;
      tickTimesPMILLIS.push({
        timeMillis: m.getTime(),
        label: daysStr,
        isHighlighted: m.getUTCDate() === 1,
      });
    }

    m.setUTCDate(m.getUTCDate() + tickIntervalDAYS);
    // m.add(tickIntervalDAYS, 'days');
  }
  return [tickTimesPMILLIS, monthTicksMillis];
};

export const getHourTickIntervals = (
  dMinPMILLIS: number,
  dMaxPMILLIS: number,
  approxTickIntervalMILLIS: number
): [ITickInterval[], ITickInterval[]] => {
  const tickIntervalMILLIS = calculateTickIntervalMillis(approxTickIntervalMILLIS);

  const ticksSince1970 = Math.floor(dMinPMILLIS / tickIntervalMILLIS);
  const firstTickPMILLIS = ticksSince1970 * tickIntervalMILLIS;
  const numTicks = Math.ceil(1 + (dMaxPMILLIS - firstTickPMILLIS) / tickIntervalMILLIS);

  const tickTimesPMILLIS: ITickInterval[] = [];
  const start = new Date(firstTickPMILLIS);
  let m = start.getTime();

  let currentDay = -1;
  const dayTicksMillis: ITickInterval[] = [];
  for (let n = 0; n < numTicks; n++) {
    m += tickIntervalMILLIS;
    const currentDate = new Date(m);
    const hours = currentDate.getUTCHours();
    const minutes = currentDate.getUTCMinutes();
    const isHighlighted = hours === 0 && minutes === 0;
    const newDay = currentDate.getUTCDate();

    const hoursStr = hours < 10 ? `0${hours}` : `${hours}`;
    const minStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
    const month = months[currentDate.getUTCMonth()];
    const day = currentDate.getUTCDate();
    const dayOfWeek = days[currentDate.getUTCDay()];
    if (newDay !== currentDay) {
      dayTicksMillis.push({
        label: `${dayOfWeek} ${month} ${day}`,
        timeMillis: m,
        isHighlighted,
      });
      currentDay = newDay;
    }

    tickTimesPMILLIS.push({
      timeMillis: m?.valueOf(),
      label: `${hoursStr}:${minStr}`,
      isHighlighted,
    });
  }
  return [tickTimesPMILLIS, dayTicksMillis];
};

export const tickIntervalRungsDays = [2, 3, 4, 5, 8, 10];

export const calculateTickInterval_DAYS = (tickIntervalMillis: number): number => {
  const tickIntervalDays = moment.duration(tickIntervalMillis, "milliseconds").asDays();
  const tickInterval = tickIntervalRungsDays.find((intervalDays: number) => {
    return tickIntervalDays <= intervalDays;
  });

  return tickInterval ? tickInterval : tickIntervalRungsDays[tickIntervalRungsDays.length - 1];
};

export const tickIntervalRungsMillis = [
  moment.duration(1, "seconds").asMilliseconds(),
  moment.duration(2, "seconds").asMilliseconds(),
  moment.duration(5, "seconds").asMilliseconds(),
  moment.duration(10, "seconds").asMilliseconds(),
  moment.duration(15, "seconds").asMilliseconds(),
  moment.duration(20, "seconds").asMilliseconds(),
  moment.duration(30, "seconds").asMilliseconds(),
  moment.duration(1, "minutes").asMilliseconds(),
  moment.duration(2, "minutes").asMilliseconds(),
  moment.duration(5, "minutes").asMilliseconds(),
  moment.duration(10, "minutes").asMilliseconds(),
  moment.duration(15, "minutes").asMilliseconds(),
  moment.duration(20, "minutes").asMilliseconds(),
  moment.duration(30, "minutes").asMilliseconds(),
  moment.duration(1, "hours").asMilliseconds(),
  moment.duration(2, "hours").asMilliseconds(),
  moment.duration(3, "hours").asMilliseconds(),
  moment.duration(6, "hours").asMilliseconds(),
  moment.duration(12, "hours").asMilliseconds(),
  moment.duration(1, "days").asMilliseconds(),
  moment.duration(1, "days").asMilliseconds(),
];

function calculateTickIntervalMillis(approxTickIntervalMILLIS: number): number {
  for (let n = 0; n < tickIntervalRungsMillis.length; n++) {
    if (approxTickIntervalMILLIS <= tickIntervalRungsMillis[n]) {
      return tickIntervalRungsMillis[n];
    }
  }
  return moment.duration(1, "days").asMilliseconds();
}

export const differenceInDays = (firstDate: number, secondDate: number): number => {
  return Math.ceil((secondDate - firstDate) / (1000 * 60 * 60 * 24));
};

export const setBeginningDay = (dateMillis: number): Date => {
  const date = new Date(dateMillis);
  date.setUTCHours(0);
  date.setUTCMinutes(0);
  return date;
};

/* Finding closest flight index */

/**
 * getClosestIndex helper function to find which index is closer to the target
 * @param flightsthe - array of flights to search for closest flight
 * @param firstIndex - the first index to compare if it's closer to target
 * @param secondIndex - the second index to compare against to see which is closer
 * @param target - the target time (in millis) to search for
 * @param comparePropthe - property to actaully compare. Either departure or arrival
 * @returns number Either firstIndex or secondIndex; whichever is closer to the target
 */
const getClosestIndex = (
  flights: IFlight[] | IGroundEvent[],
  firstIndex: number,
  secondIndex: number,
  target: number,
  compareProp: EventTimes
): number => {
  ///@ts-ignore  requires rewrite of the timeline data.
  const val1 = new Date(flights[firstIndex][compareProp]).getTime();
  ///@ts-ignore  requires rewrite of the timeline data.
  const val2 = new Date(flights[secondIndex][compareProp]).getTime();
  return target - val1 >= val2 - target ? secondIndex : firstIndex;
};

export type EventTimes = "actualStart" | "actualEnd" | "actualOut" | "actualIn";
/**
 * findClosestFlightIndex finds the closest flight with scheduledOff close to the target time
 * @param flights - the array of flights to search for closest flight
 * @param target - the target time (in millis) to search for
 * @param compareProp - the property to actaully compare. Either departure or arrival
 * @returns index returns the index of the flight that has closest time to the target
 */
export const findClosestFlightIndex = (
  flights: IFlight[] | IGroundEvent[],
  target: number,
  compareProp: EventTimes
): number => {
  const length = flights.length;

  if (!target || length === 0) return -1;

  // Corner cases
  ///@ts-ignore  requires rewrite of the timeline data.
  if (target <= new Date(flights[0][compareProp]).getTime()) return 0;
  ///@ts-ignore  requires rewrite of the timeline data.
  if (target >= new Date(flights[length - 1][compareProp]).getTime()) return length - 1;

  // Doing binary search
  let i = 0,
    j = length,
    mid = 0;
  while (i < j) {
    mid = Math.floor((i + j) / 2);
    ///@ts-ignore  requires rewrite of the timeline data.
    const midTime = new Date(flights[mid][compareProp]).getTime();
    if (target === midTime) return mid;

    /* If target is less than array element, 
        then search in left */
    if (target < midTime) {
      // If target is greater than previous
      // to mid, return closest of two
      ///@ts-ignore  requires rewrite of the timeline data.
      const previousTime = new Date(flights[mid - 1][compareProp]).getTime();
      if (mid > 0 && target > previousTime) return getClosestIndex(flights, mid - 1, mid, target, compareProp);

      /* Repeat for left half */
      j = mid;
    }
    // If target is greater than mid
    else {
      ///@ts-ignore  requires rewrite of the timeline data.
      const nextTime = new Date(flights[mid + 1][compareProp]).getTime();
      if (mid < length - 1 && target < nextTime) {
        return getClosestIndex(flights, mid, mid + 1, target, compareProp);
      }
      i = mid + 1; // update i
    }
  }

  // Only single element left after search
  return mid;
};

export const findClosestGroundIndex = (
  groundEvents: IGroundEvent[],
  targets: number[],
  compareProps: EventTimes[]
): number[] => {
  const length = groundEvents.length;

  let minIndex = -1;
  let maxIndex = -1;
  const startTarget = targets[0];
  const endTarget = targets[1];

  for (let i = 0; i < length; i++) {
    ///@ts-ignore  requires rewrite of the timeline data.
    const startTime = new Date(groundEvents[i][compareProps[0]]).getTime();
    ///@ts-ignore  requires rewrite of the timeline data.
    const endTime = new Date(groundEvents[i][compareProps[1]]).getTime();
    if (endTime > startTarget && startTime < endTarget) {
      ///@ts-ignore  requires rewrite of the timeline data.
      if (minIndex < 0 || startTime < new Date(groundEvents[minIndex][compareProps[0]]).getTime()) {
        minIndex = i;
      }
      ///@ts-ignore  requires rewrite of the timeline data.
      if (maxIndex < 0 || endTime > new Date(groundEvents[maxIndex][compareProps[1]]).getTime()) {
        maxIndex = i;
      }
    }
  }

  return [minIndex, maxIndex];
};

export const defaultAircraftRowSetting: IAircraftRowSetting = {
  rowHeightPercentage: 1,
  aircraftRowHeight: 36 + 6,
  aircraftRowPadding: 1,
  actualDepartureStart: 6 + 6,
  actualDepartureHeight: 20,
  scheduledStart: 2,
  scheduledHeight: 4 + 6,
  aircraftRowTotalHeight: 36 + 6 + 1,
};

export const getflightGLRowHeightSettings = (ratio?: number): IAircraftRowSetting => {
  return ratio
    ? {
        rowHeightPercentage: ratio,
        aircraftRowHeight: (36 + 6) * ratio,
        aircraftRowPadding: 1 * ratio,
        actualDepartureStart: (6 + 6) * ratio,
        actualDepartureHeight: 20 * ratio,
        scheduledStart: 2 * ratio,
        scheduledHeight: (4 + 6) * ratio,
        aircraftRowTotalHeight: (36 + 6 + 1) * ratio,
      }
    : defaultAircraftRowSetting;
};

export const textualFlightInfoFormat = (events: (IFlight | IGroundEvent)[], format: (string | string[])[]): string => {
  const evventText: string[] = [];
  events.forEach((e) => {
    if (e.hasOwnProperty("flightNumber")) {
      const flight = e as IFlight;
      const text = format
        .map((key) => {
          if (typeof key === "string") {
            return getValueFromObj(flight, key);
          } else {
            //We assume this is a date
            const format: string = key[0];
            const timezone: string = key[1];
            const field: string = key[2];
            const time = getValueFromObj(flight, field);
            const tz = getValueFromObj(flight, timezone);
            return moment.tz(time, tz).format(format).toUpperCase();
          }
        })
        .join("");
      evventText.push(text);
    }
  });
  return evventText.join("");
};

function getValueFromObj(obj: Record<string, any>, path: string): any {
  if (path.includes(".")) {
    const parts = path.split(".");
    let value: any = obj; // Dynamically access properties
    for (const part of parts) {
      if (value && typeof value === "object" && part in value) {
        value = value[part];
      } else {
        return path; // Return path if property not found
      }
    }
    return value;
  } else if (path in obj) {
    return obj[path];
  }
  return path;
}
